import React from "react"
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from "@chakra-ui/react"
import moment from "moment"
import { NftProps } from "lib/data"
import splitbee from "@splitbee/web"
import { LinkButton } from "components/LinkButton"

// Splitbee.js
splitbee.track("NFTCard")

// todo update the real time ownership of nft in the button
const formatPixleLink = (nft: NftProps) => {
  if (nft.release_date) {
    return nft.location.https
  }
  // otherwise use the pixle image
  // https://roampool.com/llamas/images/roam_llama_1.png
  // =>
  // https://roampool.com/llamas/pixel_images/roam_llama_1.png
  const re = /images/gi
  const replacementStr = "pixel_images"
  return nft.location.https.replace(re, replacementStr)
}

const GadgetsArea = ({ nft }: { nft: NftProps }) => {
  const owneraddr = nft.creators[0].stake_address
  const shortaddr = owneraddr.substring(0, 10) + "..." + owneraddr.substring(owneraddr.length - 10)
  return (
    <VStack spacing={1} align="stretch">
      {nft.attributes.map((attr) => (
        <HStack key={nft.id}>
          <Badge w="90px" align="right">
            {" "}
            {attr.trait_type}
          </Badge>
          <Badge key={attr.trait_type} w="75px" align="center">
            {attr.value}
          </Badge>
          <Badge w="120px">{attr.rarity_pct}% chance</Badge>
        </HStack>
      ))}

      <Badge fontSize={"lg"} align="center">
        Rarity: {nft.rarity_per_mil} / 1M
      </Badge>

      <LinkButton
        p={5}
        fontSize={"xs"}
        rounded={"full"}
        borderColor={"blue.500"}
        border={"2px"}
        boxShadow={"0px 1px 25px -5px rgb(66 153 225 / 40%), 0 10px 10px -5px rgb(66 153 225 / 43%)"}
        _hover={{
          bg: "blue.500",
        }}
        _focus={{
          bg: "blue.500",
        }}
        isTruncated
        href={"https://cardanoscan.io/stakeKey/" + owneraddr}
        onClick={() => {
          splitbee.track("NFTCard_cardanoscan_" + nft.id)
        }}
      >
        Owner: {shortaddr}
      </LinkButton>
    </VStack>
    // nft.attributes
  )
}

export const NFTModal = ({ nft }: { nft: NftProps }) => {
  return (
    <Center py={10}>
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        w={"100%"}
        height={"100%"}
        direction={{ base: "column", md: "row" }}
        bg={useColorModeValue("white", "gray.900")}
        boxShadow={"2xl"}
        padding={4}
      >
        <Flex flex={1} bg="blue.200">
          <Image loading={"lazy"} objectFit="cover" boxSize="100%" src={formatPixleLink(nft)} alt={"image"} />
        </Flex>
        <Stack flex={1} flexDirection="column" justifyContent="center" alignItems="center" p={1} pt={2}>
          <Heading fontWeight={800} size="lg" mb={4}>
            RoamLlama #{nft.id}
          </Heading>
          <Text textAlign={"center"} color={useColorModeValue("gray.700", "gray.400")}>
            {nft.release_date ? "" : "This NFT is unreleased so the image will be pixelated!"}
          </Text>
          <Text textAlign={"center"} px={3} color={useColorModeValue("gray.700", "gray.400")}>
            Released {moment(nft.release_date).fromNow()}
          </Text>

          <GadgetsArea nft={nft} />
        </Stack>
      </Stack>
    </Center>
  )
}
export const NFTCard = ({ nft }: { nft: NftProps }) => {
  const today = moment()
  const lastUpdated = moment(nft.create_date)
  const howManyDaysUpdatedAgo = today.diff(lastUpdated, "days")
  const fresh = howManyDaysUpdatedAgo < 3
  const { isOpen, onOpen, onClose } = useDisclosure()
  const owneraddr = nft.creators[0].stake_address
  const shortaddr = owneraddr.substring(0, 5) + "..." + owneraddr.substring(owneraddr.length - 5)

  return (
    <Center py={12}>
      <Box
        role={"group"}
        p={8}
        maxW={"330px"}
        w={"full"}
        bg={useColorModeValue("gray.100", "gray.700")}
        boxShadow={"2xl"}
        rounded={"lg"}
        pos={"relative"}
        zIndex={1}
        onClick={onOpen}
      >
        <Box
          rounded={"md"}
          mt={-12}
          pos={"relative"}
          height={"200px"}
          _after={{
            transition: "all .3s ease",
            content: '""',
            w: "full",
            h: "full",
            pos: "absolute",
            top: 5,
            left: 0,
            backgroundImage: `url(${formatPixleLink(nft)})`,
            filter: "blur(10px)",
            zIndex: -1,
          }}
          _groupHover={{
            _after: {
              filter: "blur(20px)",
            },
          }}
        >
          <Image
            loading={"lazy"}
            rounded={"lg"}
            height={200}
            width={282}
            objectFit={"cover"}
            src={formatPixleLink(nft)}
            alt={"nft image"}
          />
        </Box>
        <Stack pt={10} align={"center"}>
          <Heading fontSize={"xl"} fontFamily={"body"} fontWeight={500}>
            RoamLlama #{nft.id}
          </Heading>
          <Flex>
            {nft.release_date ? (
              <Button
                colorScheme="blue"
                fontSize={"xs"}
                rounded={"full"}
                isTruncated
                href={"https://cardanoscan.io/stakeKey/" + owneraddr}
                onClick={() => {
                  splitbee.track("NFTCard_cardanoscan_" + nft.id)
                }}
              >
                Released {moment(nft.release_date).fromNow()} to {shortaddr}
              </Button>
            ) : (
              <Button as={Badge} fontSize={"xs"} rounded="full">
                Unreleased!
              </Button>
            )}
          </Flex>
        </Stack>
      </Box>

      <Modal
        size={"4xl"}
        onClose={onClose}
        onOverlayClick={() => {
          splitbee.track("NFTCard_cardanoscan_" + nft.id)
        }}
        isOpen={isOpen}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody>
            <NFTModal nft={nft} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Center>
  )
}
